/**
 * "How this workflow works" (Tier 1, spec 2026-07-04 §6.2; Increment C UI pass).
 * Route: /workflows/:workflowId — read-only, mechanically projected from the
 * published definition. Content-based, kind-adaptive tabs; Configure is an
 * orthogonal edit-mode toggle (not a tab); Simulate stays its own route.
 */
import { useCallback, useMemo, useState } from 'react'
import { useParams, useSearchParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { PageFrame } from '@/components/layout/PageFrame'
import { PageHeader } from '@/components/layout/PageHeader'
import { Badge, Button, CollapsibleSection, Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui'
import { apiClient } from '@/services/apiClient'
import { useCanAccessFeature } from '@/hooks'
import { useRecordNoun } from '@/hooks/useRecordNoun'
import { useWorkflowDefinition } from '@/hooks/useWorkflowDefinition'
import { useWorkflowConfig } from '@/hooks/useWorkflowConfig'
import { useWorkflowDraft } from '@/hooks/useWorkflowDraft'
import { useWorkflowConfigStore } from '@/stores/workflowConfigStore'
import { useProcessConfigStore } from '@/stores/processConfigStore'
import { useProcessDraftStore, type AuthorableDefinitionJson } from '@/stores/processDraftStore'
import { useWorkflowsLibrary, type WorkflowSummary } from '@/hooks/useWorkflows'
import { useWorkflowVersions } from '@/hooks/useWorkflowVersions'
import { projectProcess, projectWorkflowOverview } from '@/utils/workflowOverview'
import { findingTarget, projectConfigureView, describeOverlayPath, overlayEntityLabel } from '@/utils/workflowConfigView'
import type { OrderWorkflowDefinitionJson, ProcessWorkflowDefinitionJson } from '@/types/workflowDefinition'
import type { ConfigFinding } from '@/types/workflowConfig'
import { CANDIDATE_TAG } from '@/types/workflowVersions'
import { AgendaSection, AutomationsSection, CheckpointsSection, HandoffSection, MessagesSection, SignalsSection } from './sections'
import { ConfigureBar } from './configure/ConfigureBar'
import { CarryForwardNotice, ConflictBanner } from './configure/PublishDialog'
import { CooldownEditor } from './configure/editors'
import { ParametersCard } from './configure/ParametersCard'
import { TAB_STORAGE_PREFIX, initialTabFor } from './workflowOverviewTabs'
import { VersionsTab } from './versions/VersionsTab'
import { ReadinessTab } from './ReadinessTab'
import { ProcessMap } from '@/components/process/map/ProcessMap'
import { ProcessStepList } from './process/ProcessStepList'
import { StepDrawer } from '@/components/process/StepDrawer'
import { ProcessStepEditor } from './process/ProcessStepEditor'
import { ProcessSeatsEditor } from './process/ProcessSeatsEditor'
import { ProcessFieldsEditor } from './process/ProcessFieldsEditor'
import { ProcessConfigureBar } from './process/ProcessConfigureBar'
import { ProcessDocumentEditor } from './process/ProcessDocumentEditor'
import { RawDefinitionEditor } from './process/RawDefinitionEditor'
import { WorkflowDraftBar } from './process/WorkflowDraftBar'
import { ProcessEditorToolbar } from './process/ProcessEditorToolbar'
import { FIELDS_CARD, SEATS_CARD, anchorCard, cardAnchor, stepCard, useEditorCards, type CardId } from './process/editorCards'
import { editedFromOverlay } from './process/editedCards'
import { countLine } from './process/editorSummaries'
import { ForkWorkflowDialog } from '@/components/workflows/ForkWorkflowDialog'
import { useFeatureStore } from '@/stores/featureStore'
import { indefiniteArticle } from '@/utils/article'
import { startingProcess, type StartState } from '@/utils/intakeRelation'

type WorkflowDetail = WorkflowSummary & { versions: unknown[] }

/** Mirrors `_load_authorable_workflow` (SA-381): the custom-row kinds the
 * draft lane accepts. `graph` is never authorable. */
const AUTHORABLE_KINDS = new Set(['process', 'order'])

/** Mirrors `routers/workflows.CONFIGURABLE_KINDS` (SA-386): the platform
 * kinds the Tier-2 config endpoints accept. */
const CONFIGURABLE_KINDS = new Set(['order', 'process'])

function useWorkflowDetail(workflowId: string | undefined) {
  return useQuery<WorkflowDetail, Error>({
    queryKey: ['workflows', 'detail', workflowId ?? ''],
    queryFn: () => apiClient.get<WorkflowDetail>(`/api/workflows/${workflowId}`),
    enabled: !!workflowId,
    staleTime: 60_000,
  })
}

/** SA-459 fix wave item 6: the one scroll-to-an-editor's-card helper —
 * deferred a tick so the target has rendered (a tab switch, or the editors
 * mounting). Behaviour identical to the three call sites it replaces. */
function scrollToAnchor(id: string) {
  window.setTimeout(() => document.getElementById(id)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }), 0)
}

/** SA-476 (spec D2): the "How it starts" clause says only what will happen — `find_process_for`'s rule under a loaded flag; nothing while either is unknown, or when the one binder is another process. */
function startClause(start: StartState, workflowId: string | undefined): string {
  if (start.kind === 'starts') {
    return start.process.id === workflowId
      ? ' — and the process starts at birth (email, conversation) or when Start is pressed (form).'
      : '.'
  }
  if (start.kind === 'engine_off') return '. The process engine is off on this deployment, so it does not start.'
  if (start.kind === 'ambiguous') return '. More than one process binds this intake, so none of them starts.'
  return '.'
}

export function WorkflowOverviewPage() {
  const { workflowId } = useParams<{ workflowId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const detail = useWorkflowDetail(workflowId)
  const definition = useWorkflowDefinition(workflowId)
  const canConfigure = useCanAccessFeature('workflow_configuration', 'workflows:configure')
  const canSimulate = useCanAccessFeature('workflow_configuration', 'workflows:read')
  const canVersions = useCanAccessFeature('workflow_configuration', 'workflows:read')
  // E1.5 ownership lanes (SA-371): the fork endpoint rides the workflows
  // router (workflow_engine) under workflows:author.
  const canAuthor = useCanAccessFeature('workflow_engine', 'workflows:author')
  // SA-469: the "How it starts" sentence names the record in the pack noun.
  const recordNoun = useRecordNoun().singular
  // SA-476 (spec D2): strict — the start clause is said only once the manifest says process_engine is on.
  const processEngine = useFeatureStore((s) => (s.isLoaded ? s.features.has('process_engine') : null))
  const [forkOpen, setForkOpen] = useState(false)
  // E3 Edit (spec 2026-09-18 D15, §6): a CUSTOM row's author edits the whole
  // document through the draft lane. Read eagerly so the header can say a
  // draft is waiting; re-read on Edit so the session starts on a fresh stamp.
  // An archived row is refused by the lane's row guard, so it is not offered.
  const authorable =
    canAuthor &&
    detail.data?.source === 'custom' &&
    !detail.data?.archived_at &&
    AUTHORABLE_KINDS.has(detail.data?.kind ?? '')
  const draft = useWorkflowDraft(authorable ? workflowId : undefined)
  const authoring = useProcessDraftStore((s) => s.editing && s.workflowId === workflowId)
  const draftDoc = useProcessDraftStore((s) => s.doc)
  const beginDraft = useProcessDraftStore((s) => s.begin)
  const setRawDraft = useProcessDraftStore((s) => s.setRaw)
  const [documentView, setDocumentView] = useState<'form' | 'raw'>('form')
  const [editError, setEditError] = useState<string | null>(null)
  const config = useWorkflowConfig(
    canConfigure && detail.data?.source === 'platform' && CONFIGURABLE_KINDS.has(detail.data?.kind ?? '')
      ? workflowId
      : undefined,
  )
  // Same query key as the Versions tab's own useWorkflowVersions(workflowId,
  // false) call (spec Task 7) — the cache is shared, so opening the Versions
  // tab afterward doesn't cost an extra request. Only fetched here when
  // there's a conflict to drive the ConflictBanner's promote CTA
  // (`hasCandidateVersion` below) — a viewer with no conflict never pays for
  // this at page level; the Versions tab still fetches on its own if opened.
  const hasConflict = (config.data?.last_merge_error?.length ?? 0) > 0
  const versions = useWorkflowVersions(canVersions && hasConflict ? workflowId : undefined, false)
  const editing = useWorkflowConfigStore((s) => s.editing && s.workflowId === workflowId)
  const beginEditing = useWorkflowConfigStore((s) => s.beginEditing)
  const overlay = useWorkflowConfigStore((s) => s.overlay)
  // E3 (spec 2026-09-18 §6): a process row's Customize session lives in its
  // own store — the order store's setters know nothing of steps and seats.
  const processEditing = useProcessConfigStore((s) => s.editing && s.workflowId === workflowId)
  const processOverlay = useProcessConfigStore((s) => s.overlay)
  const beginProcessEditing = useProcessConfigStore((s) => s.beginEditing)
  const setStepParam = useProcessConfigStore((s) => s.setStepParam)
  const setSeatRoles = useProcessConfigStore((s) => s.setSeatRoles)
  const setSeatLabel = useProcessConfigStore((s) => s.setSeatLabel)
  const setFieldText = useProcessConfigStore((s) => s.setFieldText)
  // SA-1060: the open state of every editor card; the page owns it because
  // the page owns every deep link into the editors.
  const cards = useEditorCards(workflowId ?? '')
  // Role names for the seats' picker. Admin-gated: a configurer who may not
  // list roles still types them (role names are data, spec §3.3).
  const roleList = useQuery<{ name: string }[], Error>({
    queryKey: ['admin', 'roles'],
    queryFn: () => apiClient.get<{ name: string }[]>('/api/admin/roles'),
    enabled: processEditing || (authoring && detail.data?.kind === 'process'),
    retry: false,
    staleTime: 60_000,
  })
  // Sticky per-workflow tab (spec 2026-07-11): survives reloads and remounts;
  // the `tabValue` availability fallback below is render-only and never
  // overwrites the stored choice. An initial `?tab=` param (Task 15) takes
  // precedence over the stored value so review findings can deep-link.
  const [activeTab, setActiveTabState] = useState(() =>
    initialTabFor(workflowId, searchParams.get('tab'), searchParams.get('step')),
  )
  const setActiveTab = (tab: string) => {
    setActiveTabState(tab)
    if (workflowId && typeof window !== 'undefined') {
      window.localStorage.setItem(`${TAB_STORAGE_PREFIX}${workflowId}`, tab)
    }
  }
  // E1 viewer (spec 2026-09-18 §5): the step a process reader has opened.
  // SA-459 (workflow UX sweep spec §5, D3): it opens the step DRAWER over
  // the page from the Overview map and from a Steps-tab row alike — no tab
  // switch; a `?step=` in the URL opens it on load (a shared link, the
  // record's map under SA-448 D11); an id the definition lacks renders
  // nothing (StepDrawer stays closed for it). The drawer opens in READ MODE
  // ONLY (fix wave item 5): a Customize or Edit session leaves this set but
  // the drawer itself stays closed, and a map click there goes to the Steps
  // tab's editors instead.
  const [selectedStep, setSelectedStep] = useState<string | null>(() => searchParams.get('step'))
  // SA-454: the URL says which step is open, so the link stays shareable and a
  // second click does not leave a stale `?step=` behind. `replace` keeps the
  // browser's Back out of the step selection.
  const selectStep = useCallback((id: string | null) => {
    setSelectedStep(id)
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (id) next.set('step', id)
      else next.delete('step')
      return next
    }, { replace: true })
  }, [setSearchParams])
  const { data: allWorkflows } = useWorkflowsLibrary()
  const workflowNames = useMemo(
    () => Object.fromEntries((allWorkflows ?? []).map((w) => [w.id, w.name])),
    [allWorkflows],
  )
  // Moved above the early returns below (rules of hooks) so ProcessMap's own
  // useMemo([vm, columns]) doesn't recompute the layout every render — keyed
  // on the definition object, which only changes when a query refetches.
  // `definition.data` may still be loading here; the gate below mirrors the
  // original `vm.kind === 'process'` check once it has (same underlying
  // definition as `processBase`, computed again after the early return for
  // the editors that need it unconditionally).
  const processDefinitionForVm = (config.data?.base_definition ??
    definition.data?.definition) as ProcessWorkflowDefinitionJson | undefined
  const processVm = useMemo(
    () =>
      processEditing && processDefinitionForVm?.kind === 'process'
        ? projectProcess(processDefinitionForVm)
        : null,
    [processEditing, processDefinitionForVm],
  )

  if (detail.isLoading || definition.isLoading) {
    return <p className="py-8 text-sm text-muted-foreground">Loading workflow…</p>
  }
  if (detail.isError || definition.isError || !detail.data || !definition.data) {
    return (
      <p className="py-8 text-sm text-destructive">
        Couldn't load this workflow — it may have no published version yet, or you may not have
        permission to view it. <Link className="underline" to="/workflows">Back to Workflows</Link>
      </p>
    )
  }

  const vm = projectWorkflowOverview(definition.data.definition)
  // Pure base for the Configure editors — the merged/published definition is
  // only a fallback until config.data (and its base_definition) loads.
  const editorBase = (config.data?.base_definition ??
    definition.data.definition) as OrderWorkflowDefinitionJson
  const processBase = (config.data?.base_definition ??
    definition.data.definition) as ProcessWorkflowDefinitionJson
  const anyEditing = editing || processEditing || authoring
  const bound = detail.data.bound_personas
  const personaName = bound[0]?.persona_name ?? null
  const configureVm =
    editing && vm.kind === 'order' ? projectConfigureView(editorBase, overlay) : null
  // SA-1060 (ruling Q3): a Customize card is edited when the sparse overlay holds an entry for it.
  const customizeEdited = processVm ? editedFromOverlay(processOverlay, processBase) : new Set<CardId>()
  const customizeCardIds = processVm ? [...processVm.steps.map((s) => stepCard(s.id)), SEATS_CARD, FIELDS_CARD] : []

  // The Customize affordance is only meaningful for platform-owned order
  // and process workflows with a published definition. Also require the config query to
  // have SUCCEEDED (not merely settled) before enabling it — starting an edit
  // session before config.data loads would seed the store with a null
  // savedDraft, and the next save would silently clobber the server draft.
  const configurable =
    canConfigure && detail.data?.source === 'platform' && CONFIGURABLE_KINDS.has(vm.kind) && config.isSuccess

  // Take ownership (spec 2026-09-18 §4.4): platform rows of the two
  // forkable kinds only — the endpoint refuses `graph` and custom rows are
  // already owned. Mirrors `fork_workflow`'s own checks so the button never
  // offers what the server would 400.
  const canTakeOwnership =
    canAuthor && detail.data?.source === 'platform' && vm.kind !== 'graph'

  // SA-84 Flow B: gated-promote CTA on the conflict banner — true when some
  // (non-archived-filtered) version already carries the reserved `candidate`
  // tag, i.e. there's something to review in Versions right now.
  const hasCandidateVersion = Array.isArray(versions.data)
    ? versions.data.some((v) => v.tags.includes(CANDIDATE_TAG))
    : false

  // Enters Customize (if not already editing) on the same base/draft
  // argument shape as the Customize button above, then jumps to the tab
  // that actually shows the thing being reviewed.
  function startEditing() {
    // SA-1410: with no stored draft the session starts from what is LIVE —
    // the live version's own overlay, not `published`, which a repin leaves
    // behind.
    const savedDraft = config.data?.draft ?? config.data?.live_overlay ?? null
    // SA-416: what the session's writes are based on. A null stamp (nothing
    // saved yet, or the seeder's pack-born row) is the lane's "absent".
    const stamp = config.data?.draft_updated_at ?? null
    const baseVersion = config.data?.base_version_number ?? null
    const liveVersion = config.data?.current_version_number ?? null
    if (vm.kind === 'process') beginProcessEditing(workflowId!, processBase, savedDraft, stamp, baseVersion, liveVersion)
    else beginEditing(workflowId!, editorBase, savedDraft, stamp, baseVersion, liveVersion)
  }
  function enterEditIfNeeded() {
    if (!anyEditing && configurable) startEditing()
  }
  /** Opens the tab, and on a process row the card, that owns an overlay path. */
  function reviewPath(path: string) {
    const target = findingTarget(path)
    setActiveTab(target.tab)
    enterEditIfNeeded()
    // A process finding names the card that owns it; the editors mount with
    // the tab, so scroll once they have rendered.
    if (target.anchor) {
      const card = anchorCard(target.anchor)
      if (card) cards.open(card)
      scrollToAnchor(target.anchor)
    }
  }
  function handleReviewFinding(finding: ConfigFinding) {
    reviewPath(finding.path)
  }
  /** SA-1060: a deep link opens the card first, so the scroll never lands on a closed row. */
  function openAndScroll(cardId: CardId) {
    cards.open(cardId)
    scrollToAnchor(cardAnchor(cardId))
  }
  // Edit: the stored draft if there is one, else the live definition — and
  // only a stored draft has a stamp (null = nothing to publish yet).
  async function startAuthoring() {
    // Edit sessions are global and survive navigation; this one would replace
    // another workflow's whole unsaved document, so ask first.
    const open = useProcessDraftStore.getState()
    if (open.editing && open.workflowId !== workflowId && open.isDirty()) {
      const proceed = window.confirm(
        'You have unsaved changes to another workflow’s draft. Editing this one discards them.',
      )
      if (!proceed) return
    }
    setEditError(null)
    // `refetch()` resolves on failure WITH the cached data — a stale "no
    // draft" would start an unstamped session over someone's newer draft.
    const result = await draft.refetch()
    if (!result.isSuccess || result.isError) {
      return setEditError("Couldn't load this workflow's draft — try Edit again.")
    }
    const data = result.data
    const doc = (data.draft ?? data.current_definition) as AuthorableDefinitionJson | null
    if (!doc) return setEditError('This workflow has no definition to edit yet.')
    beginDraft(workflowId!, doc, data.draft ? data.draft_updated_at : null, data.current_version_number)
    setDocumentView('form')
    if (vm.kind === 'process') setActiveTab('steps')
  }
  function handleReviewDraftCard(cardId: CardId) {
    setActiveTab('steps')
    setDocumentView('form')
    openAndScroll(cardId)
  }
  function handleReviewCarryForward() {
    setActiveTab('agenda')
    enterEditIfNeeded()
  }

  // SA-459 (spec §5): the drawer's footer offers the page's own entry into
  // Customize or Edit for the open step, then scrolls to that step's editor
  // card (`process-step-<id>`, `findingTarget`'s anchor). The drawer closes
  // first so the editor is what the person sees.
  function customizeStep(stepId: string) {
    selectStep(null)
    startEditing()
    setActiveTab('steps')
    openAndScroll(stepCard(stepId))
  }
  // SA-459 fix wave item 7: `startAuthoring()` can end without a session (a
  // declined confirm over another workflow's dirty draft, or a failed draft
  // fetch that leaves `editError` set) — the drawer stays open then, so the
  // person still sees what they were looking at and the error banner behind
  // it, instead of both the drawer and the editors being gone.
  async function editStep(stepId: string) {
    await startAuthoring()
    if (!useProcessDraftStore.getState().editing) return
    selectStep(null)
    handleReviewDraftCard(stepCard(stepId))
  }

  // Settings tab (edit-only Timing + any unrendered "advanced" definition keys)
  // shows in edit mode, or in read mode when there ARE unrecognized sections so
  // nothing is silently dropped (spec §9).
  const showSettingsTab = vm.kind === 'order' && (editing || vm.unrecognizedSections.length > 0)
  // Task 10 (Phase 4 readiness checklist): gated on the fetched detail's
  // manifest field (SA-27's `_summary_manifest` projection, already carried
  // by `WorkflowSummary.manifest`) — independent of workflow kind, so it's
  // appended after the kind-specific branches rather than inside either one.
  // Final-review I2 (spec §8 "flag-off → no FE tab"): ALSO gated on the same
  // feature/permission predicate as Versions — the readiness endpoint is
  // gated byte-for-byte like GET /config, so a viewer the deployment would
  // refuse never sees the tab.
  const hasManifest = !!detail.data.manifest
  const showReadiness = canVersions && hasManifest
  // E1 viewer (spec 2026-09-18 §5): a process shows Overview + Steps (+
  // Versions + Readiness). Customize, Try it, Settings and the order tabs
  // are already conditioned on the order kind; E3/E8 own process editing.
  const availableTabs = [
    ...(vm.kind === 'order'
      ? ['overview', 'agenda', 'automations', 'messages', 'handoff', 'signals', ...(showSettingsTab ? ['settings'] : []), ...(canVersions ? ['versions'] : [])]
      : vm.kind === 'process'
        ? ['overview', 'steps', ...(canVersions ? ['versions'] : [])]
        : ['overview', ...(canVersions ? ['versions'] : [])]),
    ...(showReadiness ? ['readiness'] : []),
  ]
  const tabValue = availableTabs.includes(activeTab) ? activeTab : 'overview'

  const stepDrawerActions =
    selectedStep && !anyEditing ? (
      <>
        {configurable && (
          <Button size="sm" onClick={() => customizeStep(selectedStep)}>
            Customize this step
          </Button>
        )}
        {authorable && (
          <Button size="sm" disabled={draft.isFetching} onClick={() => editStep(selectedStep)}>
            Edit this step
          </Button>
        )}
      </>
    ) : null

  return (
    <PageFrame width="detail" className="space-y-4">
      <PageHeader
        title={detail.data.name}
        sticky
        back={{ to: '/workflows', label: 'Workflows' }}
        subtitle={
          <>
            <p>
              How this workflow works — generated from the live definition
              (v{definition.data.version_number}); it can't drift from what actually runs.
            </p>
            {detail.data.source === 'custom' && detail.data.forked_from && (
              <p className="text-xs text-muted-foreground">
                Forked from {detail.data.forked_from.workflow_name} v{detail.data.forked_from.version_number} —
                this row no longer receives product updates.
              </p>
            )}
            {editError && !authoring && (
              <p role="alert" className="text-xs text-destructive">{editError}</p>
            )}
            {authorable && !authoring && draft.data?.draft && (
              <p className="text-xs text-muted-foreground">
                <Badge variant="warning">Unpublished draft</Badge>{' '}
                {draft.data.draft_updated_by_name ? `saved by ${draft.data.draft_updated_by_name}` : 'saved'}
                {draft.data.draft_updated_at ? ` on ${new Date(draft.data.draft_updated_at).toLocaleDateString()}` : ''}
                {' '}— Edit resumes it.
              </p>
            )}
          </>
        }
        actions={
          <>
            {canSimulate && vm.kind === 'order' && bound.length > 0 && !editing && !authoring && (
              <Link
                to={`/workflows/${workflowId}/simulate`}
                className="inline-flex h-8 items-center rounded-md border border-primary px-3 text-sm font-medium text-primary hover:bg-primary/10"
              >
                Try it
              </Link>
            )}
            {configurable && !anyEditing && (
              <Button size="sm" onClick={startEditing}>
                Customize
              </Button>
            )}
            {authorable && !anyEditing && (
              <Button size="sm" disabled={draft.isFetching} onClick={startAuthoring}>
                Edit
              </Button>
            )}
            {canTakeOwnership && !anyEditing && (
              <Button size="sm" variant="outline" onClick={() => setForkOpen(true)}>
                Take ownership
              </Button>
            )}
          </>
        }
      />
      {canTakeOwnership && (
        <ForkWorkflowDialog
          workflow={detail.data}
          mode="take_ownership"
          open={forkOpen}
          onOpenChange={setForkOpen}
        />
      )}

      {config.data?.last_merge_error && config.data.last_merge_error.length > 0 && (
        <ConflictBanner
          findings={config.data.last_merge_error}
          onReview={handleReviewFinding}
          showPromoteCta={hasCandidateVersion}
          onPromoteNav={() => setActiveTab('versions')}
          describe={(p) =>
            describeOverlayPath(p, config.data?.base_definition, config.data?.draft ?? config.data?.published ?? {})
          }
        />
      )}

      {config.data?.carry_forward &&
        (!config.data.last_merge_error || config.data.last_merge_error.length === 0) && (
          <CarryForwardNotice
            record={config.data.carry_forward}
            labelFor={(key) =>
              overlayEntityLabel(key, vm.kind === 'order' ? editorBase : null, config.data?.draft ?? config.data?.published ?? {})
            }
            onReview={handleReviewCarryForward}
          />
        )}

      {authoring && vm.kind !== 'process' && draftDoc && (
        // An order row is edited as a document (form editing is E9); the
        // read-only tabs below describe the PUBLISHED version meanwhile.
        <div className="space-y-2 rounded-2xl border border-border bg-card p-4">
          <h2 className="text-sm font-semibold text-foreground">Editing the definition</h2>
          <RawDefinitionEditor
            doc={draftDoc as unknown as Record<string, unknown>}
            onChange={(next) => setRawDraft(next as unknown as AuthorableDefinitionJson)}
          />
        </div>
      )}

      <Tabs value={tabValue} onValueChange={setActiveTab}>
        <TabsList className="h-auto flex-wrap">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          {vm.kind === 'order' && (
            <>
              <TabsTrigger value="agenda">Agenda</TabsTrigger>
              <TabsTrigger value="automations">Automations</TabsTrigger>
              <TabsTrigger value="messages">Messages</TabsTrigger>
              <TabsTrigger value="handoff">Handoff</TabsTrigger>
              <TabsTrigger value="signals">Signals</TabsTrigger>
              {showSettingsTab && <TabsTrigger value="settings">Settings</TabsTrigger>}
            </>
          )}
          {vm.kind === 'process' && <TabsTrigger value="steps">Steps</TabsTrigger>}
          {canVersions && <TabsTrigger value="versions">Versions</TabsTrigger>}
          {showReadiness && <TabsTrigger value="readiness">Readiness</TabsTrigger>}
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <CollapsibleSection
            title="How it starts"
            subtitle="Where and for whom this workflow runs."
            storageKey="wf-section:how-it-starts"
            variant="soft"
            icon="🚀"
          >
            {vm.kind === 'process' ? (
              // SA-469 (spec §3): the agenda ↔ process relation and the start rule, in the pack noun.
              // SA-476 (spec D2, D4): the start only when it will happen; articles agree.
              <p className="text-sm text-foreground">
                {indefiniteArticle(recordNoun, { capital: true })} {recordNoun} is born from {indefiniteArticle(vm.recordAgendaLabel)}{' '}
                <Link to={`/workflows/${vm.recordAgenda}`} className="text-primary underline-offset-2 hover:underline">
                  {vm.recordAgendaLabel}
                </Link>{' '}
                intake — an email in a bound mailbox, a pasted enquiry, or a staff conversation
                {startClause(startingProcess(vm.recordAgenda, allWorkflows, processEngine), workflowId)}
              </p>
            ) : bound.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Not attached to any persona yet — attach it from a persona's Workshop.
              </p>
            ) : (
              <ul className="space-y-1 text-sm text-foreground">
                {bound.map((bp) => (
                  <li key={bp.persona_id}>
                    <Link to={`/personas/${bp.persona_id}/workshop`} className="font-medium underline">
                      {bp.persona_name}
                    </Link>{' '}
                    on <Badge variant="secondary">{bp.channel}</Badge>
                  </li>
                ))}
              </ul>
            )}
            {vm.kind === 'order' && vm.triggerCount > 0 && (
              <p className="mt-2 text-sm text-foreground">
                Starts automatically from {vm.triggerCount} trigger{vm.triggerCount === 1 ? '' : 's'}.
              </p>
            )}
            {vm.kind === 'order' && vm.cooldownMinutes !== null && (
              <p className="mt-2 text-sm text-foreground">
                After an order completes, the workflow pauses for {vm.cooldownMinutes} minutes before a
                new one can start.
              </p>
            )}
          </CollapsibleSection>

          {vm.kind === 'order' && (
            <CheckpointsSection
              checkpoints={configureVm ? configureVm.checkpoints : vm.checkpoints}
              slotLabels={vm.slotLabels}
              editable={editing}
            />
          )}

          {vm.kind === 'graph' && (
            <CollapsibleSection
              title="Flow graph workflow"
              subtitle="This workflow is a hand-wired step graph; a full visual view is coming later."
              storageKey="wf-section:graph"
              variant="soft"
              icon="🧭"
            >
              <ul className="space-y-1 text-sm text-foreground">
                {Object.entries(vm.nodeTypeCounts).map(([type, count]) => (
                  <li key={type}>
                    <span className="font-mono text-xs">{type}</span> × {count}
                  </li>
                ))}
              </ul>
              {vm.guardrails.length > 0 && (
                <details className="mt-2 text-xs text-muted-foreground">
                  <summary className="cursor-pointer">Exact instructions to the AI, per step</summary>
                  <ul className="mt-1 space-y-1">
                    {vm.guardrails.map((g, i) => (
                      <li key={i}>
                        <span className="font-mono">{g.node}</span>: {g.text}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </CollapsibleSection>
          )}

          {vm.kind === 'process' && (
            <CollapsibleSection
              title="The steps, as a map"
              subtitle="Lanes are who works it; arrows are the named exits. Rendered from the published definition."
              storageKey="wf-section:process-map"
              variant="soft"
              icon="🗺️"
            >
              <ProcessMap
                vm={vm}
                currentStep={null}
                // SA-459 fix wave item 5: in a Customize or Edit session the
                // drawer stays closed (below), so a map click goes where a
                // click went before SA-459 — the Steps tab's editors.
                onSelect={(id) => {
                  selectStep(id)
                  if (anyEditing) {
                    setActiveTab('steps')
                    openAndScroll(stepCard(id))
                  }
                }}
              />
            </CollapsibleSection>
          )}

          {vm.kind !== 'process' && (
            <CollapsibleSection
              title="Who shapes the voice"
              subtitle="This page owns WHAT is said and done. HOW it sounds comes from the persona."
              storageKey="wf-section:voice"
              variant="soft"
              icon="🗣️"
            >
              <p className="text-sm text-foreground">
                Tone, identity, and personality come from{' '}
                {bound.length > 0 ? (
                  <Link to={`/personas/${bound[0].persona_id}/workshop`} className="font-medium underline">
                    {bound[0].persona_name}
                  </Link>
                ) : (
                  'the bound persona'
                )}{' '}
                — edit those in the Persona Workshop, not here.
              </p>
            </CollapsibleSection>
          )}

          {editing && (
            <ParametersCard
              manifest={config.data?.manifest ?? null}
              languages={editorBase.languages ?? []}
            />
          )}
        </TabsContent>

        {vm.kind === 'order' && (
          <>
            <TabsContent value="agenda">
              <AgendaSection
                personaName={personaName}
                agenda={configureVm ? configureVm.agenda : vm.agenda}
                editable={editing}
                base={editing ? editorBase : null}
                slotLabels={vm.kind === 'order' ? vm.slotLabels : undefined}
              />
            </TabsContent>

            <TabsContent value="automations">
              <AutomationsSection
                automations={configureVm ? configureVm.automations : vm.automations}
                editable={editing}
                base={editing ? editorBase : null}
                slotLabels={vm.kind === 'order' ? vm.slotLabels : undefined}
              />
            </TabsContent>

            <TabsContent value="messages">
              <MessagesSection
                messages={configureVm ? configureVm.messages : vm.messages}
                editable={editing}
                base={editing ? editorBase : null}
                slotLabels={vm.kind === 'order' ? vm.slotLabels : undefined}
              />
            </TabsContent>

            <TabsContent value="handoff">
              <HandoffSection
                personaName={personaName}
                handoff={configureVm ? configureVm.handoff : vm.handoff}
                editable={editing}
                base={editing ? editorBase : null}
                workflowNames={workflowNames}
                slotLabels={vm.kind === 'order' ? vm.slotLabels : undefined}
              />
            </TabsContent>

            <TabsContent value="signals">
              <SignalsSection
                personaName={personaName}
                signals={configureVm ? configureVm.signals : vm.signals}
                editable={editing}
                base={editing ? editorBase : null}
                slotLabels={vm.kind === 'order' ? vm.slotLabels : undefined}
              />
            </TabsContent>

            {showSettingsTab && (
              <TabsContent value="settings" className="space-y-4">
                {editing && (
                  <CollapsibleSection
                    title="Timing"
                    subtitle="How long the workflow rests after a completed order."
                    storageKey="wf-section:timing"
                    variant="soft"
                    icon="⏱️"
                  >
                    <CooldownEditor base={editorBase} />
                  </CollapsibleSection>
                )}
                {vm.unrecognizedSections.length > 0 && (
                  <CollapsibleSection
                    title="Advanced sections"
                    subtitle="Parts of this definition this page doesn't render yet."
                    summary={`${vm.unrecognizedSections.length} unrendered section${vm.unrecognizedSections.length === 1 ? '' : 's'}`}
                    defaultOpen={false}
                    storageKey="wf-section:advanced"
                    variant="soft"
                    icon="🧩"
                  >
                    <p className="font-mono text-xs text-muted-foreground">
                      {vm.unrecognizedSections.join(', ')}
                    </p>
                  </CollapsibleSection>
                )}
              </TabsContent>
            )}
          </>
        )}

        {processVm && (
          // Customize: the cards flip into editors (spec §6). Projected from
          // the PURE base, so numbers and labels never follow the draft.
          <TabsContent value="steps" className="space-y-3">
            <ProcessEditorToolbar
              line={countLine({
                steps: processVm.steps.length,
                seats: Object.keys(processBase.seats ?? {}).length,
                fields: Object.keys(processBase.fields ?? {}).length,
                edited: customizeEdited.size,
              })}
              onExpandAll={() => cards.openAll(customizeCardIds)}
              onCollapseAll={() => cards.closeAll(customizeCardIds)}
            />
            {processVm.steps.map((s) => (
              <ProcessStepEditor
                key={s.id}
                step={s}
                base={processBase}
                overlay={processOverlay}
                mode="overlay"
                onParam={setStepParam}
                cards={cards}
                edited={customizeEdited.has(stepCard(s.id))}
              />
            ))}
            <ProcessSeatsEditor
              base={processBase}
              overlay={processOverlay}
              roles={(roleList.data ?? []).map((r) => r.name)}
              onRoles={setSeatRoles}
              onLabel={setSeatLabel}
              cards={cards}
              edited={customizeEdited.has(SEATS_CARD)}
            />
            <ProcessFieldsEditor base={processBase} overlay={processOverlay} onText={setFieldText} cards={cards} edited={customizeEdited.has(FIELDS_CARD)} />
          </TabsContent>
        )}
        {vm.kind === 'process' && authoring && (
          <TabsContent value="steps">
            <ProcessDocumentEditor
              roles={(roleList.data ?? []).map((r) => r.name)}
              view={documentView}
              onViewChange={setDocumentView}
              cards={cards}
            />
          </TabsContent>
        )}
        {vm.kind === 'process' && !processVm && !authoring && (
          <TabsContent value="steps">
            <ProcessStepList vm={vm} onSelect={selectStep} />
          </TabsContent>
        )}

        {canVersions && (
          <TabsContent value="versions">
            <VersionsTab
              workflowId={workflowId!}
              isPlatformOrder={detail.data.source === 'platform' && vm.kind === 'order'}
              isPlatform={detail.data.source === 'platform'}
            />
          </TabsContent>
        )}

        {showReadiness && (
          <TabsContent value="readiness">
            <ReadinessTab workflowId={workflowId!} />
          </TabsContent>
        )}
      </Tabs>

      {/* SA-459 (spec §5): the drawer is the read-mode card. In a Customize or Edit session
          the editors own the Steps tab and the drawer stays closed; the map's click below
          keeps its pre-drawer behaviour there. */}
      {vm.kind === 'process' && !anyEditing && (
        <StepDrawer
          vm={vm}
          definition={definition.data.definition as ProcessWorkflowDefinitionJson}
          stepId={selectedStep}
          onClose={() => selectStep(null)}
          versionLabel={`v${definition.data.version_number}`}
          actions={stepDrawerActions}
        />
      )}

      {editing && workflowId && <ConfigureBar workflowId={workflowId} onReviewPath={reviewPath} />}
      {processEditing && workflowId && (
        <ProcessConfigureBar workflowId={workflowId} onReviewFinding={handleReviewFinding} onReviewPath={reviewPath} />
      )}
      {authoring && workflowId && (
        <WorkflowDraftBar
          workflowId={workflowId}
          onReviewCard={vm.kind === 'process' ? handleReviewDraftCard : undefined}
        />
      )}
    </PageFrame>
  )
}
